import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Tabs,
  Switch,
  InputNumber,
  Row,
  Col,
  Divider,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  AppstoreOutlined,
  ContainerOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import lightappService from '../../services/lightappService';

const { Option } = Select;
const { TextArea } = Input;

const Templates = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await lightappService.getTemplates();
      if (response.success) {
        setTemplates(response.data || []);
      } else {
        message.error(response.error || 'Failed to fetch templates');
      }
    } catch (error) {
      message.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingTemplate(record);

    // Parse JSON fields for form
    const defaultConfig = typeof record.default_config === 'string'
      ? JSON.parse(record.default_config)
      : record.default_config;

    const envSchema = typeof record.env_schema === 'string'
      ? JSON.parse(record.env_schema)
      : record.env_schema;

    form.setFieldsValue({
      ...record,
      default_config: JSON.stringify(defaultConfig, null, 2),
      env_schema: JSON.stringify(envSchema, null, 2),
      stack_compose: record.stack_compose || '',
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await lightappService.deleteTemplate(id);
      if (response.success) {
        message.success('Template deleted successfully');
        fetchTemplates();
      } else {
        message.error(response.error || 'Failed to delete template');
      }
    } catch (error) {
      message.error('Failed to delete template');
    }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      const response = await lightappService.syncTemplates();
      if (response.success) {
        message.success('Templates synced successfully');
        fetchTemplates();
      } else {
        message.error(response.error || 'Failed to sync templates');
      }
    } catch (error) {
      message.error('Failed to sync templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Parse JSON fields
      let defaultConfig = {};
      let envSchema = {};

      if (values.default_config) {
        try {
          defaultConfig = JSON.parse(values.default_config);
        } catch (e) {
          message.error('Invalid JSON in Default Config');
          return;
        }
      }

      if (values.env_schema) {
        try {
          envSchema = JSON.parse(values.env_schema);
        } catch (e) {
          message.error('Invalid JSON in Environment Schema');
          return;
        }
      }

      const data = {
        ...values,
        default_config: JSON.stringify(defaultConfig),
        env_schema: JSON.stringify(envSchema),
        is_active: values.is_active ? 1 : 0,
      };

      let response;
      if (editingTemplate) {
        response = await lightappService.updateTemplate(editingTemplate.id, data);
      } else {
        response = await lightappService.createTemplate(data);
      }

      if (response.success) {
        message.success(
          `Template ${editingTemplate ? 'updated' : 'created'} successfully`
        );
        setModalVisible(false);
        fetchTemplates();
      } else {
        message.error(response.error || 'Failed to save template');
      }
    } catch (error) {
      message.error('Failed to save template');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {record.template_type === 'container' ? <ContainerOutlined /> : <AppstoreOutlined />}
          <span style={{ fontWeight: 500 }}>{record.display_name || text}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'template_type',
      key: 'template_type',
      render: (type) => (
        <Tag color={type === 'container' ? 'blue' : 'purple'}>
          {type === 'container' ? 'Container' : 'Stack'}
        </Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      ellipsis: true,
      render: (image) => (
        <code style={{ fontSize: '12px', color: '#666' }}>{image}</code>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="应用模板管理"
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={handleSync} loading={loading}>
              Sync from Registry
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Template
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No templates yet. Click “Add Template” or “Sync from Registry”."
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingTemplate ? 'Edit Template' : 'Add Template'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={900}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Basic Info" key="1">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Template Name"
                    rules={[{ required: true, message: 'Please enter template name' }]}
                  >
                    <Input placeholder="e.g., wordpress" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="display_name"
                    label="Display Name"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="e.g., WordPress" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="template_type"
                    label="Template Type"
                    rules={[{ required: true }]}
                    initialValue="container"
                  >
                    <Select>
                      <Option value="container">Container (Single)</Option>
                      <Option value="stack">Stack (Multi-container)</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="type"
                    label="Category"
                    rules={[{ required: true }]}
                    initialValue="web"
                  >
                    <Select>
                      <Option value="web">Web</Option>
                      <Option value="database">Database</Option>
                      <Option value="cache">Cache</Option>
                      <Option value="devops">DevOps</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="image"
                label="Docker Image"
                rules={[{ required: true }]}
              >
                <Input placeholder="e.g., wordpress:latest" />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <TextArea rows={3} placeholder="Template description" />
              </Form.Item>

              <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Configuration" key="2">
              <Form.Item
                name="default_config"
                label="Default Configuration (JSON)"
                tooltip="Container config or resource allocation for Stack"
              >
                <TextArea
                  rows={10}
                  placeholder={JSON.stringify({
                    container_config: {
                      ports: ['${WEB_PORT}:80'],
                      volumes: ['/tmp/docker-volumes/${SERVICE_ID}:/var/www/html'],
                      network_mode: 'bridge',
                    },
                    resource_allocation: {
                      wordpress: { memory_ratio: 0.7, cpu_ratio: 0.7, var_prefix: 'WP' },
                      db: { memory_ratio: 0.3, cpu_ratio: 0.3 },
                    },
                  }, null, 2)}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Environment Variables" key="3">
              <Form.Item
                name="env_schema"
                label="Environment Schema (JSON)"
                tooltip="Define required and optional environment variables"
              >
                <TextArea
                  rows={10}
                  placeholder={JSON.stringify({
                    ENV_WEB_PORT: {
                      label: 'Web Port',
                      type: 'port',
                      default: 'auto',
                      required: false,
                    },
                    ENV_DB_PASSWORD: {
                      label: 'Database Password',
                      type: 'password',
                      default: 'random',
                      required: true,
                    },
                  }, null, 2)}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Stack Compose" key="4">
              <Form.Item
                name="stack_compose"
                label="Docker Compose File (for Stack type only)"
                tooltip="YAML content for docker-compose"
              >
                <TextArea
                  rows={15}
                  placeholder={`version: '3.8'
services:
  wordpress:
    image: wordpress:latest
    mem_limit: \${WP_MEMORY_LIMIT}m
    mem_reservation: \${WP_MEMORY_RESERVATION}m
    cpus: "\${WP_CPU_LIMIT}"
    environment:
      WORDPRESS_DB_HOST: db
      WORDPRESS_DB_PASSWORD: \${DB_PASSWORD}
    labels:
      - "traefik.enable=true"
      - "traefik.http.routers.service-\${SERVICE_ID}.rule=Host(\`\${DOMAIN}\`)"
    volumes:
      - wordpress_data:/var/www/html

  db:
    image: mysql:8.0
    mem_limit: \${DB_MEMORY_LIMIT}m
    environment:
      MYSQL_ROOT_PASSWORD: \${DB_ROOT_PASSWORD}
      MYSQL_DATABASE: wordpress
      MYSQL_PASSWORD: \${DB_PASSWORD}
    volumes:
      - db_data:/var/lib/mysql

volumes:
  wordpress_data:
  db_data:`}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default Templates;
